/**
 * ProjectSelector.tsx — Projektauswahl nach Login
 *
 * Zwei Ansichten: Raster (Grid) und Liste (Table).
 * Light-Theme, Ghost-CMS-inspired Typography (PP Mori sans).
 */

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { useAuthStore, type Project, type AuthUser } from '../../store/auth'
import { apiFetch, API_URL } from '../../lib/apiBase'
import { hasModuleAccess } from '../layout/Sidebar'
import { ProjectMap } from './ProjectMap'
import { ApiSettings } from './ApiSettings'
import { UserAvatar } from '../presence/UserAvatar'
import type { PresenceUser } from '../../store/presence'

// ─── Helpers ────────────────────────────────────────────────────────────────

function toCount(v: unknown): number {
  if (typeof v === 'number') return v
  if (Array.isArray(v)) return v.length
  return 0
}

function relativeTime(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const diff = Date.now() - d.getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'gerade eben'
  if (mins < 60) return `vor ${mins} Min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `vor ${hrs} Std`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'gestern'
  if (days < 7) return `vor ${days} Tagen`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `vor ${weeks} Wo`
  const months = Math.floor(days / 30)
  return `vor ${months} Mo`
}

function timeGroup(iso?: string): 'today' | 'week' | 'older' {
  if (!iso) return 'older'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'older'
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const ts = d.getTime()
  if (ts >= startOfToday) return 'today'
  if (ts >= startOfToday - 6 * 86_400_000) return 'week'
  return 'older'
}

const GROUP_LABELS: Record<string, string> = {
  today: 'Heute',
  week: 'Diese Woche',
  older: 'Älter',
}

// ─── Risikobewertung (hardcoded bis API verfügbar) ─────────────────────────

interface RiskData {
  level: 'Gering' | 'Mittel' | 'Hoch'
  percent: number
  color: string
  recommendation: string
}

const RISK_DATA: Record<string, RiskData> = {
  RH5: {
    level: 'Mittel', percent: 52, color: '#e67e22',
    recommendation: 'Gebäudegutachten für Schadstoffe und Ertüchtigung vervollständigen.',
  },
  KHD: {
    level: 'Gering', percent: 14, color: '#4a7c3f',
    recommendation: 'Sehr gute Datenbasis — Vollständigkeit laufend sichern.',
  },
  PTK: {
    level: 'Hoch', percent: 74, color: '#c0392b',
    recommendation: 'Plandaten und Gutachten fehlen weitgehend — Erfassung starten.',
  },
  ARS: {
    level: 'Mittel', percent: 38, color: '#e67e22',
    recommendation: 'Bestandsdokumentation erweitern — Fotodokumentation und Raumverzeichnis ergänzen.',
  },
}

function getRisk(project: Project): RiskData | null {
  return RISK_DATA[project.code || ''] || RISK_DATA[project.name] || null
}

function statusConfig(status?: string) {
  const s = (status || '').toLowerCase()
  if (s.includes('audit'))
    return { gridLabel: 'Audit läuft', listLabel: 'Audit läuft', badgeClass: 'ps-badge--audit', dotClass: 'ps-dot--green' }
  if (s.includes('incomplete') || s.includes('unvollst'))
    return { gridLabel: 'Scan unvollst.', listLabel: 'Scan unvollst.', badgeClass: 'ps-badge--incomplete', dotClass: 'ps-dot--orange' }
  if (s.includes('new') || s.includes('neu'))
    return { gridLabel: 'Neu', listLabel: 'Neu', badgeClass: 'ps-badge--new', dotClass: 'ps-dot--blue' }
  return { gridLabel: 'Scene aktiv', listLabel: 'Aktiv', badgeClass: 'ps-badge--active', dotClass: 'ps-dot--green' }
}

/** Get the best available thumbnail URL/data for a project */
function projectThumb(p: Project, size: 'small' | 'medium' = 'medium'): string | null {
  if (size === 'small') return p.thumbnailSmall || p.thumbnailMedium || p.thumbnailUrl || null
  return p.thumbnailHigh || p.thumbnailMedium || p.thumbnailUrl || p.thumbnailSmall || null
}

const ADMIN_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5174/'
    : 'https://admin.ortoai.administer.cloud/'

// ─── Fallback Preview (seeded dots) ─────────────────────────────────────────

function FallbackPreview({ seed }: { seed: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const w = canvas.clientWidth
    const h = canvas.clientHeight
    canvas.width = w * dpr
    canvas.height = h * dpr
    ctx.scale(dpr, dpr)

    ctx.fillStyle = '#f3f2f0'
    ctx.fillRect(0, 0, w, h)

    let s = 0
    for (let i = 0; i < seed.length; i++) s = ((s << 5) - s + seed.charCodeAt(i)) | 0
    const next = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff }

    // Subtle grid
    ctx.strokeStyle = 'rgba(0,0,0,0.04)'
    ctx.lineWidth = 0.5
    const step = 30
    for (let x = step; x < w; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke() }
    for (let y = step; y < h; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke() }

    // Dots
    const cx = w / 2, cy = h * 0.52
    for (let i = 0; i < 220; i++) {
      const angle = next() * Math.PI * 2
      const radius = next() * Math.min(w, h) * 0.36
      const x = cx + Math.cos(angle) * radius * (0.5 + next() * 0.6)
      const y = cy + Math.sin(angle) * radius * (0.3 + next() * 0.5)
      const r = 0.7 + next() * 1.4
      const c = next()
      ctx.fillStyle = c < 0.5 ? 'rgba(0,0,0,0.13)' : c < 0.7 ? 'rgba(59,130,246,0.25)' : c < 0.85 ? 'rgba(234,88,12,0.22)' : 'rgba(220,38,38,0.18)'
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
    }
  }, [seed])

  return <canvas ref={canvasRef} className="ps-card__canvas" />
}

// ─── Icons (inline SVG) ─────────────────────────────────────────────────────

const IconGrid = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
  </svg>
)

const IconList = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
)

const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
)

const IconFilter = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
)

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const IconBackend = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
  </svg>
)

const IconLogout = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

// ─── Profile Overlay ────────────────────────────────────────────────────────

function ProfileOverlay({ user, onClose }: { user: AuthUser; onClose: () => void }) {
  const updateUser = useAuthStore((s) => s.updateUser)
  const [email, setEmail] = useState(user.email || '')
  const [username, setUsername] = useState(user.username || '')
  const [firstName, setFirstName] = useState(user.firstName || '')
  const [lastName, setLastName] = useState(user.lastName || '')
  const [position, setPosition] = useState(user.position || '')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    if (password && password !== passwordConfirm) {
      setError('Passwörter stimmen nicht überein')
      return
    }

    setSaving(true)
    try {
      await updateUser(
        { email, username, firstName, lastName, position },
        password || undefined
      )
      setSuccess(true)
      setPassword('')
      setPasswordConfirm('')
      setTimeout(() => setSuccess(false), 2000)
    } catch (err: any) {
      setError(err.message || 'Speichern fehlgeschlagen')
    } finally {
      setSaving(false)
    }
  }

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="ps-profile-backdrop" onClick={onClose}>
      <div className="ps-profile" onClick={(e) => e.stopPropagation()}>
        <div className="ps-profile__header">
          <h2 className="ps-profile__title">Profil bearbeiten</h2>
          <button className="ps-profile__close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form className="ps-profile__form" onSubmit={handleSave}>
          <div className="ps-profile__row">
            <div className="ps-profile__field">
              <label className="ps-profile__label">E-Mail</label>
              <input className="ps-profile__input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
          </div>

          <div className="ps-profile__row">
            <div className="ps-profile__field">
              <label className="ps-profile__label">Username</label>
              <input className="ps-profile__input" type="text" value={username} onChange={(e) => setUsername(e.target.value)} required />
            </div>
          </div>

          <div className="ps-profile__row ps-profile__row--half">
            <div className="ps-profile__field">
              <label className="ps-profile__label">Vorname</label>
              <input className="ps-profile__input" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="ps-profile__field">
              <label className="ps-profile__label">Nachname</label>
              <input className="ps-profile__input" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="ps-profile__row">
            <div className="ps-profile__field">
              <label className="ps-profile__label">Position</label>
              <input className="ps-profile__input" type="text" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="z.B. Projektleiter" />
            </div>
          </div>

          <div className="ps-profile__divider" />

          <div className="ps-profile__row ps-profile__row--half">
            <div className="ps-profile__field">
              <label className="ps-profile__label">Neues Passwort</label>
              <input className="ps-profile__input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
            </div>
            <div className="ps-profile__field">
              <label className="ps-profile__label">Passwort bestätigen</label>
              <input className="ps-profile__input" type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
            </div>
          </div>

          {error && <div className="ps-profile__error">{error}</div>}

          <div className="ps-profile__actions">
            <button className="ps-profile__btn ps-profile__btn--ghost" type="button" onClick={onClose}>Abbrechen</button>
            <button className="ps-profile__btn ps-profile__btn--primary" type="submit" disabled={saving}>
              {saving ? 'Speichern …' : success ? 'Gespeichert' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────────────────────

type ViewMode = 'grid' | 'list'
type StatusFilter = 'all' | 'active' | 'audit' | 'new'

export function ProjectSelector() {
  const projects = useAuthStore((s) => s.projects)
  const loadProjects = useAuthStore((s) => s.loadProjects)
  const loadProjectThumbnails = useAuthStore((s) => s.loadProjectThumbnails)
  const updateProjectThumbnail = useAuthStore((s) => s.updateProjectThumbnail)
  const selectProject = useAuthStore((s) => s.selectProject)
  const logout = useAuthStore((s) => s.logout)
  const user = useAuthStore((s) => s.user)
  const userPermissions = (user?.permissions || []) as string[]
  const canCreateProject = hasModuleAccess('module:project-create', userPermissions)
  const canOpenApiSettings = hasModuleAccess('module:api-settings', userPermissions)

  const [view, setView] = useState<ViewMode>('grid')
  const [showSettings, setShowSettings] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [loading, setLoading] = useState<string | null>(null)
  const [showProfile, setShowProfile] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [buildingCategories, setBuildingCategories] = useState<Record<string, string>>({})
  const [projectCounts, setProjectCounts] = useState<Record<string, { audits: number; notes: number; documents: number }>>({})
  const [presenceMap, setPresenceMap] = useState<Record<string, PresenceUser[]>>({})
  const searchRef = useRef<HTMLInputElement>(null)

  // Aktiv anwesende User pro Projekt — alle 10s aktualisieren
  useEffect(() => {
    let cancelled = false
    const fetchPresence = async () => {
      try {
        const r = await apiFetch('/api/projects/presence')
        if (!r.ok) return
        const json = await r.json()
        const data: Record<string, PresenceUser[]> = json.data || json || {}
        if (!cancelled) setPresenceMap(data)
      } catch { /* ignore */ }
    }
    fetchPresence()
    const id = window.setInterval(fetchPresence, 10_000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [])

  // ── New-Project-Modal State ─────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createName, setCreateName] = useState('')
  const [createDesc, setCreateDesc] = useState('')
  const [availableClouds, setAvailableClouds] = useState<Array<{ id: string; name: string }>>([])
  const [cloudsLoadError, setCloudsLoadError] = useState<string | null>(null)
  const [selectedClouds, setSelectedClouds] = useState<Set<string>>(new Set())
  const [cloudSearch, setCloudSearch] = useState('')

  const openCreate = useCallback(async () => {
    setShowCreate(true)
    setCreateError(null); setCloudsLoadError(null)
    setCreateName(''); setCreateDesc(''); setSelectedClouds(new Set()); setCloudSearch('')
    // Pointclouds laden — bei fehlender Permission still scheitern lassen
    try {
      const res = await apiFetch(`/api/pointclouds`)
      if (res.ok) {
        const json = await res.json()
        const list: Array<{ id: string; name: string }> = Array.isArray(json.data?.data)
          ? json.data.data
          : Array.isArray(json.data)
            ? json.data
            : []
        setAvailableClouds(list.map((c: any) => ({ id: c.id, name: c.name })))
      } else {
        setCloudsLoadError('Pointcloud-Liste konnte nicht geladen werden (fehlende Berechtigung).')
        setAvailableClouds([])
      }
    } catch {
      setCloudsLoadError('Pointcloud-Liste konnte nicht geladen werden.')
      setAvailableClouds([])
    }
  }, [])

  const submitCreate = useCallback(async () => {
    if (!createName.trim() || creating) return
    setCreating(true); setCreateError(null)
    try {
      const groupIds = (user?.groups || []).map((g) => g.id)
      const res = await apiFetch(`/api/projects`, {
        method: 'POST',
        body: JSON.stringify({
          name: createName.trim(),
          description: createDesc.trim() || undefined,
          groupIds,                                  // automatische Zuweisung an User-Gruppen
          pointCloudIds: Array.from(selectedClouds), // ausgewählte Pointclouds
          // etlServerId: bewusst weglassen → Backend füllt Default-Server
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error?.message || `Anlegen fehlgeschlagen (HTTP ${res.status})`)
      }
      const json = await res.json()
      const created = json.data || json
      setShowCreate(false)
      await loadProjects()
      // Direkt ins neue Projekt springen
      if (created?.id) {
        try { await selectProject(created.id) }
        catch { /* Listen-Anzeige reicht als Fallback */ }
      }
    } catch (e: any) {
      setCreateError(e?.message || 'Projekt konnte nicht angelegt werden')
    } finally {
      setCreating(false)
    }
  }, [createName, createDesc, selectedClouds, user, loadProjects, selectProject, creating])

  const filteredClouds = useMemo(() => {
    const q = cloudSearch.trim().toLowerCase()
    if (!q) return availableClouds
    return availableClouds.filter((c) => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q))
  }, [availableClouds, cloudSearch])

  // Bauwerkskategorien + Counts laden
  useEffect(() => {
    if (projects.length === 0) return
    let cancelled = false
    ;(async () => {
      const cats: Record<string, string> = {}
      const counts: Record<string, { audits: number; notes: number; documents: number }> = {}
      for (const p of projects) {
        if (cancelled) break
        // Building data (Kategorie)
        try {
          const res = await apiFetch(`${API_URL}/api/projects/${p.id}/building-data`)
          if (res.ok) {
            const json = await res.json()
            const bd = json.data || json
            const cat = bd?.fields?.bauwerkskategorie
            if (cat && typeof cat === 'string') cats[p.id] = cat
          }
        } catch { /* ignore */ }

        // Counts (annotations, documents)
        try {
          const [annRes, docRes] = await Promise.all([
            apiFetch(`${API_URL}/api/projects/${p.id}/annotations`),
            apiFetch(`${API_URL}/api/projects/${p.id}/documents`),
          ])
          let audits = 0, notes = 0, documents = 0
          if (annRes.ok) {
            const annJson = await annRes.json()
            const anns: any[] = Array.isArray(annJson.data || annJson) ? (annJson.data || annJson) : []
            audits = anns.filter((a: any) => a.type === 'audit').length
            notes = anns.filter((a: any) => a.type !== 'audit').length
          }
          if (docRes.ok) {
            const docJson = await docRes.json()
            const docs: any[] = Array.isArray(docJson.data || docJson) ? (docJson.data || docJson) : []
            documents = docs.length
          }
          counts[p.id] = { audits, notes, documents }
        } catch { /* ignore */ }

        // Incremental update
        if (!cancelled) {
          setBuildingCategories({ ...cats })
          setProjectCounts({ ...counts })
        }
      }
    })()
    return () => { cancelled = true }
  }, [projects])

  // Projekte laden, danach Thumbnails nachladen
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      await loadProjects()
      if (!cancelled) {
        // Thumbnails nur nachladen wenn noch keine vorhanden
        const current = useAuthStore.getState().projects
        const needsThumbs = current.some((p) => !p.thumbnailHigh && !p.thumbnailMedium && !p.thumbnailSmall)
        if (needsThumbs) loadProjectThumbnails()
      }
    })()
    return () => { cancelled = true }
  }, [loadProjects, loadProjectThumbnails])

  // ── Live-Update: Projekt-Thumbnail aktualisieren, wenn die Startszene
  //                 des Projekts ein neues Thumbnail erhält.
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      const { sceneId, thumbnailSmall, thumbnailMedium, thumbnailHigh } = detail
      if (!sceneId) return

      // Scene aus dem Scene-Store holen (lazy import, damit auth-Flow
      // nicht auf Scene-Store angewiesen ist)
      const { useSceneStore } = await import('../../store/scenes')
      const scene = useSceneStore.getState().scenes.find((s) => s.id === sceneId)
      if (!scene?.isStartScene || !scene.projectId) return

      updateProjectThumbnail(scene.projectId, {
        thumbnailSmall,
        thumbnailMedium,
        thumbnailHigh,
      })
    }
    window.addEventListener('orto:scene-thumbnail-updated', handler)
    return () => window.removeEventListener('orto:scene-thumbnail-updated', handler)
  }, [updateProjectThumbnail])

  // ⌘K to focus search
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const filtered = useMemo(() => {
    let list = [...projects]
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q)
      )
    }
    if (statusFilter !== 'all') {
      list = list.filter((p) => {
        const s = (p.status || '').toLowerCase()
        if (statusFilter === 'active') return s === 'active' || s === 'scene_active' || !s
        if (statusFilter === 'audit') return s.includes('audit')
        if (statusFilter === 'new') return s === 'new'
        return true
      })
    }
    list.sort((a, b) => {
      const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
      const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
      return tb - ta
    })
    return list
  }, [projects, search, statusFilter])

  const counts = useMemo(() => {
    const c = { all: projects.length, active: 0, audit: 0, new: 0 }
    for (const p of projects) {
      const s = (p.status || '').toLowerCase()
      if (s.includes('audit')) c.audit++
      else if (s === 'new') c.new++
      else c.active++
    }
    return c
  }, [projects])

  const grouped = useMemo(() => {
    const groups: { key: string; label: string; items: Project[] }[] = []
    const map: Record<string, Project[]> = {}
    for (const p of filtered) {
      const g = timeGroup(p.updatedAt)
      if (!map[g]) map[g] = []
      map[g].push(p)
    }
    for (const key of ['today', 'week', 'older'] as const) {
      if (map[key]?.length) groups.push({ key, label: GROUP_LABELS[key], items: map[key] })
    }
    if (groups.length === 0 && filtered.length > 0) {
      groups.push({ key: 'all', label: '', items: filtered })
    }
    return groups
  }, [filtered])

  const handleSelect = useCallback(async (projectId: string) => {
    setLoading(projectId)
    setError(null)
    try {
      await selectProject(projectId)
    } catch (err: any) {
      setError(err.message || 'Projekt laden fehlgeschlagen')
    } finally {
      setLoading(null)
    }
  }, [selectProject])

  const initials = user
    ? (user.firstName && user.lastName
        ? `${user.firstName[0]}${user.lastName[0]}`
        : (user.email || 'U').substring(0, 2)
      ).toUpperCase()
    : '?'

  const lastOpenedLabel = useMemo(() => {
    const savedId = localStorage.getItem('currentProjectId')
    if (!savedId) return null
    const now = new Date()
    const h = String(now.getHours()).padStart(2, '0')
    const m = String(now.getMinutes()).padStart(2, '0')
    return `zuletzt geöffnet heute, ${h}:${m}`
  }, [])

  return (
    <div className="ps">
      {/* ── Navbar ──────────────────────────────────────────── */}
      <nav className="ps__navbar">
        <div className="ps__navbar-left">
          <img className="ps__navbar-logo" src="/assets/logo_black.svg" alt="orto" />
          <span className="ps__navbar-divider" />
          <span className="ps__navbar-section">V4.12 · BUILD 2026.04.B</span>
        </div>
        <div className="ps__navbar-right">
          <span className="ps__navbar-user-label">Angemeldet als</span>
          <span className="ps__navbar-user-email" onClick={() => setShowProfile(true)}>{user?.email || user?.username}</span>
          <span className="ps__navbar-avatar" onClick={() => setShowProfile(true)}>{initials}</span>
          {canOpenApiSettings && (
            <button className="ps__navbar-btn" onClick={() => setShowSettings(true)} title="Einstellungen">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </button>
          )}
          <button className="ps__navbar-btn" onClick={logout}>
            <IconLogout />
            <span>Abmelden</span>
          </button>
        </div>
      </nav>

      {/* ── Main ───────────────────────────────────────────── */}
      <main className="ps__main">
        <div className="ps__header">
          <div>
            <h1 className="ps__title">Projekt auswählen</h1>
            <p className="ps__subtitle">
              {projects.length} Projekt{projects.length !== 1 ? 'e' : ''}
              {lastOpenedLabel && <> · <strong>{lastOpenedLabel}</strong></>}
            </p>
          </div>
          <div className="ps__view-toggle">
            <button className={`ps__view-btn ${view === 'grid' ? 'ps__view-btn--active' : ''}`} onClick={() => setView('grid')}>
              <IconGrid /> <span>Raster</span>
            </button>
            <button className={`ps__view-btn ${view === 'list' ? 'ps__view-btn--active' : ''}`} onClick={() => setView('list')}>
              <IconList /> <span>Liste</span>
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="ps__filters">
          <div className="ps__filters-left">
            <span className="ps__filters-tab">
              Projekte <span className="ps__filters-count">{projects.length}</span>
            </span>
            <div className="ps__search">
              <IconSearch />
              <input
                ref={searchRef}
                className="ps__search-input"
                type="text"
                placeholder="Projekt, Ort oder Nummer"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <kbd className="ps__search-kbd">⌘K</kbd>
            </div>
            <div className="ps__status-pills">
              {([
                ['all', 'Alle', counts.all],
                ['active', 'Aktiv', counts.active],
                ['audit', 'Audit', counts.audit],
                ['new', 'Neu', counts.new],
              ] as [StatusFilter, string, number][]).map(([key, label, count]) => (
                <button
                  key={key}
                  className={`ps__pill ${statusFilter === key ? 'ps__pill--active' : ''}`}
                  onClick={() => setStatusFilter(key)}
                >
                  {label} {count}
                </button>
              ))}
            </div>
            <button className="ps__filter-btn">
              <IconFilter /> <span>Filter</span>
            </button>
          </div>
          {canCreateProject && (
            <button className="ps__new-btn" onClick={openCreate}>
              <IconPlus /> <span>Neues Projekt</span>
            </button>
          )}
        </div>

        {error && <div className="ps__error">{error}</div>}

        {filtered.length === 0 && (
          <p className="ps__empty">{projects.length === 0 ? 'Keine Projekte verfügbar.' : 'Keine Projekte gefunden.'}</p>
        )}

        {/* ── Grid View ────────────────────────────────────── */}
        {view === 'grid' && filtered.length > 0 && (
          <div className="ps-grid">
            {filtered.map((p) => {
              const sc = statusConfig(p.status)
              const clouds = p.pointClouds?.length || 0
              const pc = projectCounts[p.id]
              const audits = pc ? pc.audits : toCount(p.audits)
              const notes = pc ? pc.notes : toCount(p.notes)
              const docs = pc ? pc.documents : toCount(p.documents)
              const thumb = projectThumb(p)
              const presentUsers = presenceMap[p.id] || []
              return (
                <button
                  key={p.id}
                  className={`ps-card ${loading === p.id ? 'ps-card--loading' : ''}`}
                  onClick={() => handleSelect(p.id)}
                  onMouseEnter={() => window.dispatchEvent(new CustomEvent('ps:hover-project', { detail: { projectId: p.id } }))}
                  disabled={loading !== null}
                  style={{ position: 'relative' }}
                >
                  {presentUsers.length > 0 && (
                    <div
                      style={{
                        position: 'absolute', right: 8, bottom: 8, zIndex: 2,
                        display: 'flex', gap: 2, padding: 3,
                        background: 'rgba(255,255,255,0.92)',
                        backdropFilter: 'blur(4px)',
                        borderRadius: 999,
                        boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {presentUsers.slice(0, 4).map((u) => (
                        <UserAvatar key={u.id} user={u} size={20} />
                      ))}
                      {presentUsers.length > 4 && (
                        <span style={{
                          minWidth: 20, height: 20, padding: '0 5px', borderRadius: 999,
                          background: '#e2e8f0', color: '#475569', fontSize: 10, fontWeight: 600,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}>+{presentUsers.length - 4}</span>
                      )}
                    </div>
                  )}
                  <div className="ps-card__preview">
                    {thumb
                      ? <img className="ps-card__thumb" src={thumb} alt="" />
                      : <FallbackPreview seed={p.id || p.name} />
                    }
                    <span className={`ps-badge ${sc.badgeClass}`}>{buildingCategories[p.id] || sc.gridLabel}</span>
                    <span className="ps-card__compass">
                      <svg width="10" height="10" viewBox="0 0 10 10"><polygon points="5,0 10,10 0,10" fill="rgba(0,0,0,0.25)" /></svg>
                    </span>
                  </div>
                  <div className="ps-card__body">
                    <div className="ps-card__name-row">
                      <span className="ps-card__name">{p.name}</span>
                      <span className="ps-card__code">{p.code || ''}</span>
                    </div>
                    {p.description && <span className="ps-card__desc">{p.description}</span>}
                    <div className="ps-card__divider" />
                    <div className="ps-card__stats">
                      <span className="ps-card__stat ps-card__stat--accent">{clouds} Punktwolken</span>
                      <span className="ps-card__stat">{audits} Audits</span>
                      <span className="ps-card__stat">{notes} Notes</span>
                      <span className="ps-card__stat">{docs} Reports</span>
                    </div>
                    {p.updatedAt && <div className="ps-card__time">{relativeTime(p.updatedAt)}</div>}
                  </div>
                  {(() => {
                    const risk = getRisk(p)
                    if (!risk) return null
                    return (
                      <>
                        <div className="ps-card__risk">
                          <span className="ps-card__risk-label">Risikobewertung</span>
                          <span className="ps-card__risk-value" style={{ color: risk.color }}>{risk.level} · {risk.percent}%</span>
                          <div className="ps-card__risk-bar">
                            <div className="ps-card__risk-fill" style={{ width: `${risk.percent}%`, background: risk.color }} />
                          </div>
                        </div>
                        <div className="ps-card__risk-rec" style={{ background: risk.color + '0D' }}>
                          {risk.recommendation}
                        </div>
                      </>
                    )
                  })()}
                </button>
              )
            })}
          </div>
        )}

        {/* ── List View ────────────────────────────────────── */}
        {view === 'list' && filtered.length > 0 && (
          <div className="ps-list">
            <div className="ps-list__header">
              <span className="ps-list__col ps-list__col--project">Projekt</span>
              <span className="ps-list__col ps-list__col--status">Status</span>
              <span className="ps-list__col ps-list__col--num">Punktwolken</span>
              <span className="ps-list__col ps-list__col--num">Audits</span>
              <span className="ps-list__col ps-list__col--num">Notes</span>
              <span className="ps-list__col ps-list__col--num">Reports</span>
              <span className="ps-list__col ps-list__col--time">Geändert</span>
            </div>

            {grouped.map((group) => (
              <React.Fragment key={group.key}>
                {group.label && <div className="ps-list__group">{group.label}</div>}
                {group.items.map((p) => {
                  const sc = statusConfig(p.status)
                  const clouds = p.pointClouds?.length || 0
                  const pc = projectCounts[p.id]
                  const audits = pc ? pc.audits : toCount(p.audits)
                  const notes = pc ? pc.notes : toCount(p.notes)
                  const docs = pc ? pc.documents : toCount(p.documents)
                  const thumb = projectThumb(p)
                  return (
                    <button
                      key={p.id}
                      className={`ps-list__row ${loading === p.id ? 'ps-list__row--loading' : ''}`}
                      onClick={() => handleSelect(p.id)}
                      onMouseEnter={() => window.dispatchEvent(new CustomEvent('ps:hover-project', { detail: { projectId: p.id } }))}
                      disabled={loading !== null}
                    >
                      <span className="ps-list__col ps-list__col--project">
                        <span className="ps-list__thumb">
                          {thumb
                            ? <img className="ps-list__thumb-img" src={thumb} alt="" />
                            : <span className="ps-list__thumb-label">{(p.code || p.name).substring(0, 4).toUpperCase()}</span>
                          }
                        </span>
                        <span className="ps-list__info">
                          <span className="ps-list__name">
                            {p.name}
                            {p.code && <span className="ps-list__code">{p.code}</span>}
                          </span>
                          {p.description && <span className="ps-list__desc">{p.description}</span>}
                        </span>
                      </span>
                      <span className="ps-list__col ps-list__col--status">
                        <span className={`ps-dot ${sc.dotClass}`} />
                        <span className="ps-list__status-label">{sc.listLabel}</span>
                      </span>
                      <span className="ps-list__col ps-list__col--num">{clouds}</span>
                      <span className="ps-list__col ps-list__col--num">{audits}</span>
                      <span className="ps-list__col ps-list__col--num">{notes}</span>
                      <span className="ps-list__col ps-list__col--num">{docs}</span>
                      <span className="ps-list__col ps-list__col--time">{relativeTime(p.updatedAt)}</span>
                    </button>
                  )
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </main>

      {/* Project Map */}
      <ProjectMap />

      {/* Profile Overlay */}
      {showProfile && user && (
        <ProfileOverlay user={user} onClose={() => setShowProfile(false)} />
      )}

      {showSettings && (
        <ApiSettings
          onClose={() => setShowSettings(false)}
          onApply={() => {
            setShowSettings(false)
            // Reload: Auth-Store neu laden mit neuer API-URL
            window.location.reload()
          }}
        />
      )}

      {/* ── Modal: Neues Projekt anlegen ──────────────────────────────── */}
      {showCreate && (
        <div
          onClick={() => !creating && setShowCreate(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, padding: 24, width: '92%', maxWidth: 560,
              maxHeight: '90vh', overflow: 'auto',
              fontFamily: 'system-ui, sans-serif',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Neues Projekt</h3>
              <button
                onClick={() => !creating && setShowCreate(false)}
                disabled={creating}
                style={{ border: 0, background: 'transparent', cursor: creating ? 'not-allowed' : 'pointer', fontSize: 22, opacity: creating ? 0.4 : 1 }}
              >×</button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#475569', marginBottom: 4 }}>Name *</label>
              <input
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                autoFocus
                placeholder="Projektname"
                disabled={creating}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14 }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#475569', marginBottom: 4 }}>Beschreibung</label>
              <textarea
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                rows={2}
                placeholder="Optional"
                disabled={creating}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            <div style={{ marginBottom: 16, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>
                  Pointclouds {selectedClouds.size > 0 && `(${selectedClouds.size} ausgewählt)`}
                </label>
              </div>
              {cloudsLoadError && (
                <p style={{ fontSize: 12, color: '#d97706', margin: '4px 0 8px' }}>{cloudsLoadError}</p>
              )}
              {availableClouds.length > 0 && (
                <input
                  type="text"
                  value={cloudSearch}
                  onChange={(e) => setCloudSearch(e.target.value)}
                  placeholder="Suchen…"
                  disabled={creating}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, marginBottom: 8 }}
                />
              )}
              {availableClouds.length === 0 && !cloudsLoadError && (
                <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Keine Pointclouds verfügbar.</p>
              )}
              {filteredClouds.length > 0 && (
                <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                  {filteredClouds.map((c) => (
                    <label
                      key={c.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 10px', borderBottom: '1px solid #f1f5f9',
                        cursor: creating ? 'not-allowed' : 'pointer', fontSize: 13,
                        background: selectedClouds.has(c.id) ? '#f8fafc' : '#fff',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedClouds.has(c.id)}
                        onChange={() => {
                          const next = new Set(selectedClouds)
                          if (next.has(c.id)) next.delete(c.id); else next.add(c.id)
                          setSelectedClouds(next)
                        }}
                        disabled={creating}
                      />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{c.id}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 16px' }}>
              Gruppen werden automatisch deinen Gruppen zugewiesen ({(user?.groups || []).length || 0}); ETL-Server: Standard.
            </p>

            {createError && (
              <p style={{ fontSize: 13, color: '#dc2626', margin: '0 0 12px' }}>{createError}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowCreate(false)}
                disabled={creating}
                style={{ padding: '8px 16px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: creating ? 'not-allowed' : 'pointer', fontSize: 13 }}
              >Abbrechen</button>
              <button
                onClick={submitCreate}
                disabled={!createName.trim() || creating}
                style={{
                  padding: '8px 16px', border: 0, borderRadius: 4,
                  background: !createName.trim() || creating ? '#cbd5e1' : '#1a1a1a',
                  color: '#fff', cursor: !createName.trim() || creating ? 'not-allowed' : 'pointer', fontSize: 13,
                }}
              >{creating ? 'Wird angelegt…' : 'Anlegen'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
